import { borderRadius, customSizes, palette, spaces } from '@kimuno/theme';
import { Box, Drawer, SwipeableDrawer as MuiSwipeableDrawer } from '@mui/material';
import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

type DrawerHeight = 'half' | 'large' | 'full';

const DRAWER_HEIGHTS: Record<DrawerHeight, string> = {
	half: '50vh',
	large: '80vh',
	full: '100vh',
};

const DRAWER_Z_INDEX = 1401;

interface SwipeableDrawerProps {
	open: boolean;
	children: ReactNode | ReactNode[];
	onClose: () => void;
	onOpen?: () => void;
	pullSnap?: boolean;
	initialHeight?: DrawerHeight;
	draggable?: boolean;
	backgroundColor?: string;
	'data-testid'?: string;
}

const SwipeableDrawer = ({
	open,
	onClose,
	onOpen,
	children,
	pullSnap = false,
	initialHeight = 'half',
	draggable = true,
	backgroundColor,
	'data-testid': dataTestId,
}: SwipeableDrawerProps) => {
	const [drawerHeight, setDrawerHeight] = useState<DrawerHeight>(initialHeight);
	const [isDragging, setIsDragging] = useState(false);
	const [startY, setStartY] = useState(0);
	const [currentY, setCurrentY] = useState(0);
	const drawerRef = useRef<HTMLDivElement>(null);

	// Reset height when drawer closes
	useEffect(() => {
		if (!open) {
			setDrawerHeight(initialHeight);
		}
	}, [open, initialHeight]);

	const handleTouchStart = (e: React.TouchEvent) => {
		if (!pullSnap) return;
		setIsDragging(true);
		setStartY(e.touches[0].clientY);
		setCurrentY(e.touches[0].clientY);
	};

	const handleTouchMove = (e: React.TouchEvent) => {
		if (!pullSnap || !isDragging) return;
		setCurrentY(e.touches[0].clientY);
	};

	// Shared by the touch-end and mouse-up paths: snap between heights on a
	// large-enough drag, or close when dragged down from the smallest height.
	const handleDragEnd = useCallback(() => {
		if (!pullSnap || !isDragging) return;

		const deltaY = startY - currentY;
		const threshold = 50;

		if (Math.abs(deltaY) > threshold) {
			if (deltaY > 0) {
				// Swiping up - expand
				if (drawerHeight === 'half') {
					setDrawerHeight('large');
				} else if (drawerHeight === 'large') {
					setDrawerHeight('full');
				}
			} else {
				// Swiping down - contract
				if (drawerHeight === 'full') {
					setDrawerHeight('large');
				} else if (drawerHeight === 'large') {
					setDrawerHeight('half');
				} else {
					onClose();
				}
			}
		}

		setIsDragging(false);
		setStartY(0);
		setCurrentY(0);
	}, [pullSnap, isDragging, startY, currentY, drawerHeight, onClose]);

	const handleMouseDown = (e: React.MouseEvent) => {
		if (!pullSnap) return;
		setIsDragging(true);
		setStartY(e.clientY);
		setCurrentY(e.clientY);
	};

	const handleMouseMove = useCallback((e: MouseEvent) => {
		setCurrentY(e.clientY);
	}, []);

	useEffect(() => {
		if (isDragging && pullSnap) {
			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleDragEnd);
			return () => {
				document.removeEventListener('mousemove', handleMouseMove);
				document.removeEventListener('mouseup', handleDragEnd);
			};
		}
		return undefined;
	}, [isDragging, pullSnap, handleMouseMove, handleDragEnd]);

	const currentHeight = pullSnap ? DRAWER_HEIGHTS[drawerHeight] : customSizes.swipeableDrawer.height;

	const sharedPaperStyles = {
		paddingX: spaces.md,
		paddingBottom: spaces.xl,
		backgroundColor,
	};

	const drawerStyles = {
		zIndex: DRAWER_Z_INDEX,
		'& .MuiDrawer-paper': {
			...sharedPaperStyles,
			borderTopLeftRadius: pullSnap && drawerHeight === 'full' ? 0 : borderRadius.md,
			borderTopRightRadius: pullSnap && drawerHeight === 'full' ? 0 : borderRadius.md,
			height: currentHeight,
			transition: isDragging ? 'none' : 'height 0.3s ease-in-out, border-radius 0.3s ease-in-out',
		},
	};

	const dragIndicatorStyles = {
		width: customSizes.swipeableDrawer.dragIndicatorWidth,
		height: customSizes.swipeableDrawer.dragIndicatorHeight,
		backgroundColor: palette.elevation.outlined,
		borderRadius: borderRadius.xs,
		margin: `${spaces.xs} auto ${spaces.xxs}`,
		cursor: pullSnap ? (isDragging ? 'grabbing' : 'grab') : 'default',
	};

	const dragIndicatorContainerStyles = pullSnap
		? {
				position: 'absolute' as const,
				top: 0,
				left: 0,
				right: 0,
				height: spaces.xl,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				touchAction: 'none',
				userSelect: 'none',
				zIndex: 1,
			}
		: {};

	// Fixed full-height variant: no drag handle, no snap, and no gesture or
	// escape dismissal, so the content behind the drawer is never revealed.
	if (!draggable) {
		return (
			<Drawer
				anchor="bottom"
				open={open}
				onClose={() => {}}
				data-testid={dataTestId}
				ModalProps={{
					keepMounted: true,
				}}
				sx={{
					zIndex: DRAWER_Z_INDEX,
					'& .MuiDrawer-paper': {
						...sharedPaperStyles,
						height: DRAWER_HEIGHTS.full,
					},
				}}
			>
				{children}
			</Drawer>
		);
	}

	return (
		<MuiSwipeableDrawer
			ref={drawerRef}
			anchor="bottom"
			open={open}
			onClose={onClose}
			onOpen={onOpen || (() => {})}
			disableSwipeToOpen
			data-testid={dataTestId}
			ModalProps={{
				keepMounted: true,
			}}
			sx={drawerStyles}
		>
			{pullSnap ? (
				<Box
					onTouchStart={handleTouchStart}
					onTouchMove={handleTouchMove}
					onTouchEnd={handleDragEnd}
					onMouseDown={handleMouseDown}
					sx={dragIndicatorContainerStyles}
				>
					<Box data-testid="swipeable-drawer-drag-indicator" sx={dragIndicatorStyles} />
				</Box>
			) : (
				<Box data-testid="swipeable-drawer-drag-indicator" sx={dragIndicatorStyles} />
			)}
			<Box sx={{ paddingTop: pullSnap ? spaces.xl : 0 }}>{children}</Box>
		</MuiSwipeableDrawer>
	);
};

export default SwipeableDrawer;
